import { ChevronLeft } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { LoginCard } from '../../molecules/LoginCard';
import { PasswordField } from '../../molecules/PasswordField';

export interface PasswordLoginFormProps {
  /** Pre-filled email address (displayed as read-only) */
  email: string;
  /** Callback when the form is submitted with password */
  onSubmit?: (password: string) => void;
  /** Callback when forgot password link is clicked */
  onForgotPassword?: () => void;
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Whether the form is in loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string;
  /** Title text (default: "Sign in") */
  title?: string;
  /** Password input placeholder (default: "Password") */
  passwordPlaceholder?: string;
  /** Continue button text (default: "Continue") */
  continueButtonText?: string;
  /** Forgot password link text (default: "Forgot Password?") */
  forgotPasswordText?: string;
  /** Back button text (default: "Back") */
  backButtonText?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * PasswordLoginForm Organism
 *
 * The password entry step of the login flow containing:
 * - "Sign In" title
 * - Pre-filled email display (read-only appearance)
 * - Password input with visibility toggle
 * - Continue button (orange)
 * - "Forgot Password?" link
 * - Back button
 *
 * @example
 * ```tsx
 * <PasswordLoginForm
 *   email="admin@dawnsecurity.co"
 *   onSubmit={(password) => handleLogin(password)}
 *   onForgotPassword={() => navigate('/forgot-password')}
 *   onBack={() => setStep('email')}
 * />
 * ```
 */
export const PasswordLoginForm = React.forwardRef<HTMLDivElement, PasswordLoginFormProps>(
  (
    {
      email,
      onSubmit,
      onForgotPassword,
      onBack,
      loading = false,
      error,
      title = 'Sign in',
      passwordPlaceholder = 'Password',
      continueButtonText = 'Continue',
      forgotPasswordText = 'Forgot Password?',
      backButtonText = 'Back',
      className,
    },
    ref
  ) => {
    const [password, setPassword] = React.useState('');
    const [hasError, setHasError] = React.useState(false);
    const passwordInputRef = React.useRef<HTMLInputElement>(null);

    const handleSubmit = React.useCallback(() => {
      if (!password) {
        setHasError(true);
        return;
      }
      setHasError(false);
      onSubmit?.(password);
    }, [password, onSubmit]);

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          handleSubmit();
        }
      },
      [handleSubmit]
    );

    const handlePasswordChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (hasError) setHasError(false);
      },
      [hasError]
    );

    const displayError = !!error || hasError;

    return (
      <LoginCard ref={ref} className={cn('justify-between', className)}>
        {/* Top section: Title + Form fields */}
        <div className="flex flex-col gap-10 items-center w-full">
          {/* Sign in title */}
          <p className="font-outfit font-medium text-[16px] leading-[1.2] text-dawn-black capitalize">
            {title}
          </p>

          {/* Form fields container */}
          <div className="flex flex-col gap-5 items-start w-full">
            {/* Email display (read-only appearance) */}
            <div className="w-full h-10 rounded border border-solid border-dawn-black flex items-center px-2">
              <span className="font-outfit font-normal text-[13px] leading-[1.3] text-dawn-black">
                {email}
              </span>
            </div>

            {/* Password input with error message */}
            <div className="flex flex-col gap-2 items-start w-full">
              <PasswordField
                ref={passwordInputRef}
                autoFocus
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyDown}
                placeholder={passwordPlaceholder}
                disabled={loading}
                error={displayError}
              />
              {displayError && (
                <p className="font-outfit text-[10px] font-normal capitalize leading-[1.2] text-error">
                  {error || 'Please enter your password'}
                </p>
              )}
            </div>

            {/* Continue button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={cn(
                'w-full h-10 bg-dawn-orange rounded flex items-center justify-center',
                'hover:bg-dawn-orange-hover transition-colors duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {loading ? (
                <span className="animate-spin w-[18px] h-[18px] border-2 border-dawn-white border-t-transparent rounded-full" />
              ) : (
                <span className="font-outfit font-medium text-[14px] leading-[1.2] text-dawn-white capitalize">
                  {continueButtonText}
                </span>
              )}
            </button>

            {/* Forgot Password link */}
            {onForgotPassword && (
              <button
                onClick={onForgotPassword}
                disabled={loading}
                className={cn(
                  'font-outfit font-medium text-[13px] leading-[1.3] text-grey-400',
                  'hover:text-dawn-black transition-colors duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {forgotPasswordText}
              </button>
            )}
          </div>
        </div>

        {/* Back button at bottom */}
        {onBack && (
          <button
            onClick={onBack}
            disabled={loading}
            className={cn(
              'flex items-center gap-0.5 text-grey-400',
              'hover:text-dawn-black transition-colors duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <ChevronLeft className="w-3 h-3" />
            <span className="font-outfit font-medium text-[13px] leading-[1.3] text-center">
              {backButtonText}
            </span>
          </button>
        )}
      </LoginCard>
    );
  }
);

PasswordLoginForm.displayName = 'PasswordLoginForm';

export default PasswordLoginForm;
